import ArrowIcon from "@mui/icons-material/ArrowForwardIosSharp";
import {
  Card,
  CardContent,
  CardHeader,
  Collapse,
  Grid,
  Stack,
  TextField,
  Typography,
  debounce,
  styled
} from "@mui/material";
import React, { useCallback } from "react";

import { DynamicContentTab, EnhancedDynamicContentItem } from "../../Types/DynamicContentView";
import StyledReactMarkdown from "../Helpers/StyledReactMarkdown";

interface Props {
  dynamicContentTab: DynamicContentTab;
  setDynamicContentTab: (content: DynamicContentTab) => void;
}
const DynamicContentPanelTab: React.FC<Props> = ({ dynamicContentTab, setDynamicContentTab }: Props) => {
  const debouncedFilter = useCallback(
    debounce((filter: string) => setDynamicContentTab(DynamicContentTab.searchContent(dynamicContentTab, filter)), 400),
    [dynamicContentTab]
  );
  const childSetDynamicContent = (index: number) => (child: EnhancedDynamicContentItem) => {
    if (!dynamicContentTab.content) return;
    const content = [...dynamicContentTab.content];
    content[index] = child;
    setDynamicContentTab({ ...dynamicContentTab, content });
  };
  return (
    <Grid container direction="column" spacing={1} wrap="nowrap">
      <Grid item sx={{ width: "100%" }}>
        <TextField
          label="Search"
          onChange={({ target: { value } }) => debouncedFilter(value)}
          sx={{ mx: 2, minWidth: "15rem", maxWidth: "100%" }}
        />
      </Grid>
      <Grid item xs>
        <Stack spacing={1}>
          {dynamicContentTab.content.map(
            (dynamicContentItem, index) =>
              !dynamicContentItem.hidden && (
                <DynamicContentPanelItem
                  dynamicContentItem={dynamicContentItem}
                  expanded={dynamicContentTab.showChildIndex === index}
                  onClick={() =>
                    setDynamicContentTab({
                      ...dynamicContentTab,
                      showChildIndex: dynamicContentTab.showChildIndex === index ? undefined : index
                    })
                  }
                  setDynamicContent={childSetDynamicContent(index)}
                  key={index}
                />
              )
          )}
        </Stack>
      </Grid>
    </Grid>
  );
};

const expandArrowClass = "expand-arrow";
const cardExpandedClass = "card-expanded";

interface ListItemProps {
  dynamicContentItem: EnhancedDynamicContentItem;
  expanded: boolean;
  onClick: (isExpanded: boolean) => void;
  setDynamicContent: (item: EnhancedDynamicContentItem) => void;
}

const DynamicContentPanelItem: React.FC<ListItemProps> = ({
  dynamicContentItem,
  expanded,
  onClick,
  setDynamicContent
}: ListItemProps) => {
  const childSetDynamicContent = (index: number) => (child: EnhancedDynamicContentItem) => {
    if (!dynamicContentItem.children) return;
    const children = [...dynamicContentItem.children];
    children[index] = child;
    setDynamicContent({ ...dynamicContentItem, children });
  };
  return !dynamicContentItem.hidden ? (
    <StyledCard className={expanded ? cardExpandedClass : undefined}>
      <CardHeader
        title={
          <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="center">
            <Typography variant="h6">{dynamicContentItem.title}</Typography>
            <ArrowIcon className={expandArrowClass} />
          </Stack>
        }
        onClick={onClick}
      />
      <Collapse in={expanded} unmountOnExit>
        <CardContent>
          <Stack spacing={2}>
            {Boolean(dynamicContentItem.content.length) && (
              <StyledReactMarkdown>
                {dynamicContentItem.content.reduce((prev, current) => `${prev}\n\n${current}`)}
              </StyledReactMarkdown>
            )}
            {dynamicContentItem.children?.map((child, index) => (
              <DynamicContentPanelItem
                dynamicContentItem={child}
                expanded={dynamicContentItem.showChildIndex === index}
                onClick={expanded =>
                  setDynamicContent({ ...dynamicContentItem, showChildIndex: expanded ? index : undefined })
                }
                setDynamicContent={childSetDynamicContent(index)}
                key={index}
              />
            ))}
          </Stack>
        </CardContent>
      </Collapse>
    </StyledCard>
  ) : (
    <></>
  );
};

const StyledCard = styled(Card)(({ theme }) => ({
  marginLeft: `${theme.spacing(0.4)} !important`,
  marginRight: `${theme.spacing(0.4)} !important`,
  [`&.${cardExpandedClass} > .MuiCardHeader-root .${expandArrowClass}`]: { transform: "rotate(90deg)" },
  "& .MuiCardHeader-root": { backgroundColor: theme.palette.action.focus },
  "& .MuiCardContent-root": { padding: theme.spacing(1) }
}));

export default DynamicContentPanelTab;
